// vue-i18n message assembly, mirroring the config system's default + brand
// override + deepMerge pattern (see src/config/index.ts):
//
//   src/localeDefaults/<locale>/<namespace>.json  - checked into git, shared
//                                                    baseline for every brand
//   src/locale/<locale>/<namespace>.json           - brand-specific overrides,
//                                                    copied in per-brand by
//                                                    `npm run brand:switch`
//                                                    from brand-switch/<brand>/
//                                                    locale/ (gitignored here,
//                                                    since it's generated)
//
// A brand's override file only needs to contain the keys that actually differ
// from the default - deepMerge fills in everything else. An override file can
// be `{}` if a brand has no differences for that namespace at all, which is
// also how a brand opts into a language whose wording it doesn't customise.
import { deepMerge } from '@/config/deepMerge';

const defaultsContext = import.meta.glob('../localeDefaults/**/*.json', {
  eager: true,
});
const overridesContext = import.meta.glob('./**/*.json', { eager: true });

// Walks the import.meta.glob result of `<locale>/<namespace>.json` files and
// builds { [locale]: { [namespace]: <parsed JSON> } }. Used for both the
// defaults context and the overrides context, since they're laid out the same way.
const loadNamespaces = (
  ctx: Record<string, unknown>,
): Record<string, Record<string, any>> => {
  const result: Record<string, Record<string, any>> = {};

  for (const [key, mod] of Object.entries(ctx)) {
    const match = key.match(/([^/]+)\/([^/]+)\.json$/);
    if (!match) continue;

    const [, locale, namespace] = match;

    if (!result[locale]) {
      result[locale] = {};
    }
    result[locale][namespace] = (mod as { default: unknown }).default;
  }

  return result;
};

const defaults = loadNamespaces(defaultsContext);
const overrides = loadNamespaces(overridesContext);

// The locales the active brand offers. The defaults carry a full translation
// for every locale, so every language would otherwise be selectable by every
// brand; presence of a brand override directory is what opts a brand in.
export const brandLocales = Object.keys(overrides);

// RFC 4647 "Lookup": progressively truncate the requested tag until one the
// brand actually offers matches, so a stored `en-US` on a brand that only ships
// `en-GB` still lands on English rather than the first locale alphabetically.
// Falls back to whatever the brand does offer if nothing matches at all.
export const resolveLocale = (tag: string): string => {
  let range = tag;

  while (range) {
    const match = brandLocales.find(
      (locale) => locale === range || locale.startsWith(`${range}-`),
    );
    if (match) return match;

    const lastSeparator = range.lastIndexOf('-');
    if (lastSeparator === -1) break;
    range = range.slice(0, lastSeparator);
  }

  return brandLocales[0] ?? 'en';
};

const messages: Record<string, any> = {};

// Union of locales/namespaces from both sides, in case a brand's override
// introduces a locale or namespace the defaults don't have (or vice versa).
for (const locale of new Set([
  ...Object.keys(defaults),
  ...Object.keys(overrides),
])) {
  messages[locale] = {};

  for (const namespace of new Set([
    ...Object.keys(defaults[locale] ?? {}),
    ...Object.keys(overrides[locale] ?? {}),
  ])) {
    messages[locale][namespace] = deepMerge(
      defaults[locale]?.[namespace] ?? {},
      overrides[locale]?.[namespace],
    );
  }
}

export default {
  messages,
};
